import {
  BellAlertIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  DocumentCheckIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  HandRaisedIcon,
  HandThumbDownIcon,
  HeartIcon,
  InformationCircleIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
} from '@heroicons/react/24/solid';
import { format, parseISO } from 'date-fns';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  type BlockedByYouItem,
  type BriefItem,
  type OverdueBreakdownItem,
  type TaskMetrics,
  useGetTodaysBriefQuery,
  useSubmitThumbsDownMutation,
  type WeeklyDueItem,
} from '@/services/api/presidentialBriefService';
import { selectUser } from '@/state/selectors/root-selectors';

// ---------------------------------------------------------------------------
// Animation styles (injected once via singleton)
// ---------------------------------------------------------------------------

const ANIMATION_STYLES = `
@keyframes briefFadeUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes briefScaleIn {
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes briefSlideRight {
  from { opacity: 0; transform: translateX(-8px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes briefGrowWidth {
  from { width: 0; }
}
@keyframes briefGrowHeight {
  from { height: 0; }
}
@keyframes briefCountUp {
  from { opacity: 0; transform: scale(0.5); }
  to { opacity: 1; transform: scale(1); }
}
.brief-card { animation: briefScaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
.brief-card-d1 { animation-delay: 0ms; }
.brief-card-d2 { animation-delay: 80ms; }
.brief-card-d3 { animation-delay: 160ms; }
.brief-card-d4 { animation-delay: 240ms; }
.brief-item { animation: briefSlideRight 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
.brief-footer { animation: briefFadeUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) 320ms both; }
.brief-bar-h { animation: briefGrowWidth 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
.brief-bar-v { animation: briefGrowHeight 0.6s cubic-bezier(0.16, 1, 0.3, 1) both; }
.brief-stat { animation: briefCountUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) both; }
`;

let stylesInjected = false;
const useInjectStyles = () => {
  useEffect(() => {
    if (stylesInjected) return;
    stylesInjected = true;
    const style = document.createElement('style');
    style.setAttribute('data-brief-animations', '');
    style.textContent = ANIMATION_STYLES;
    document.head.appendChild(style);
  }, []);
};

// ---------------------------------------------------------------------------
// Category config
// ---------------------------------------------------------------------------

const CATEGORY_CONFIG: Record<
  BriefItem['category'],
  { icon: React.ElementType; label: string }
> = {
  demand: { icon: DocumentCheckIcon, label: 'Demand Ready' },
  email: { icon: EnvelopeIcon, label: 'Needs Reply' },
  treatment: { icon: HeartIcon, label: 'Check-in' },
  records: { icon: DocumentTextIcon, label: 'Records' },
  coverage: { icon: ShieldCheckIcon, label: 'Coverage' },
  agent: { icon: SparklesIcon, label: 'Review Needed' },
  task: { icon: ExclamationTriangleIcon, label: 'Action Needed' },
  client: { icon: BellAlertIcon, label: 'Client Alert' },
  calendar: { icon: CalendarDaysIcon, label: 'Calendar' },
};

const useLiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
};

// ---------------------------------------------------------------------------
// Brief item row
// ---------------------------------------------------------------------------

const BriefItemRow = ({
  item,
  isLast,
  index = 0,
}: {
  item: BriefItem;
  isLast: boolean;
  index?: number;
}) => {
  const config = CATEGORY_CONFIG[item.category];
  const Icon = config.icon;
  const isClickable = item.link_type && item.link_id;
  const handleClick = () => {
    if (!item.link_type || !item.link_id) return;
    if (item.link_type === 'matter')
      window.open(`/matters/${item.link_id}`, '_blank');
    else if (item.link_type === 'task')
      window.open(`/tasks?task=${item.link_id}`, '_blank');
  };
  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      className={`brief-item ${isClickable ? 'cursor-pointer' : ''}`}
      style={{ animationDelay: `${200 + index * 60}ms` }}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter') handleClick();
      }}
    >
      <div className="flex gap-3 py-5">
        <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-black/80" />
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-semibold text-black">
            {config.label}
            {item.matter_name && (
              <span className="font-medium text-black/50">
                {' '}
                &middot; {item.matter_name}
              </span>
            )}
          </p>
          <p className="mt-1 text-[15px] font-medium leading-[1.5] text-black/70">
            {item.text}
          </p>
        </div>
      </div>
      {!isLast && <div className="ml-8 h-px bg-black/[0.06]" />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// For You card
// ---------------------------------------------------------------------------

const ForYouCard = ({ items }: { items: BriefItem[] }) => (
  <div className="flex h-full flex-col rounded-2xl bg-[#F7F7F8]">
    <div className="flex items-center gap-3 px-7 pb-1 pt-7">
      <LightBulbIcon className="h-5 w-5 text-black" />
      <p className="text-xl font-bold tracking-tight text-black">For you</p>
    </div>
    <div className="scrollbar-thin flex-1 overflow-y-auto px-7 pb-5">
      {items.map((item, i) => (
        <BriefItemRow
          key={i}
          item={item}
          isLast={i === items.length - 1}
          index={i}
        />
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Blocked by you card
// ---------------------------------------------------------------------------

const BlockedByYouCard = ({ items }: { items: BlockedByYouItem[] }) => (
  <div className="flex h-full flex-col rounded-2xl bg-[#F7F7F8]">
    <div className="flex items-center gap-3 px-7 pb-1 pt-7">
      <HandRaisedIcon className="h-5 w-5 text-black" />
      <p className="text-xl font-bold tracking-tight text-black">
        Blocked by you
      </p>
    </div>
    <div className="scrollbar-thin flex-1 overflow-y-auto px-7 pb-5">
      {items.length > 0 ? (
        items.map((item, i) => {
          const isClickable = item.task_id || item.matter_id;
          const handleClick = () => {
            if (item.task_id)
              window.open(`/tasks?task=${item.task_id}`, '_blank');
            else if (item.matter_id)
              window.open(`/matters/${item.matter_id}`, '_blank');
          };
          return (
            <div
              key={i}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              className={`brief-item ${isClickable ? 'cursor-pointer' : ''}`}
              style={{ animationDelay: `${280 + i * 60}ms` }}
              onClick={handleClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleClick();
              }}
            >
              <div className="flex gap-3 py-5">
                {item.kind === 'email' ? (
                  <EnvelopeIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-black/80" />
                ) : (
                  <UserIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-black/80" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-[15px] font-semibold text-black">
                    {item.who_is_waiting}
                  </p>
                  <p className="mt-0.5 truncate text-[14px] font-medium text-black/70">
                    {item.title}
                  </p>
                  <p className="mt-0.5 text-[13px] font-medium text-black/40">
                    {item.subtitle}
                  </p>
                </div>
              </div>
              {i < items.length - 1 && (
                <div className="ml-8 h-px bg-black/[0.06]" />
              )}
            </div>
          );
        })
      ) : (
        <div className="flex h-full items-center justify-center">
          <p className="text-[15px] font-medium text-black/50">
            Nothing blocked
          </p>
        </div>
      )}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// On Your Plate — stats + stacked bar + weekly workload
// ---------------------------------------------------------------------------

const WeeklyBarChart = ({ data }: { data: WeeklyDueItem[] }) => {
  const maxCount = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex h-[120px] items-end justify-between gap-3 pt-2">
      {data.map((d, i) => {
        const heightPct = Math.max((d.count / maxCount) * 100, 8);
        return (
          <div key={i} className="flex flex-1 flex-col items-center gap-2">
            <div className="flex h-[90px] w-full items-end justify-center">
              <div
                className={`group/bar brief-bar-v relative w-full max-w-[48px] origin-bottom rounded-lg transition-colors ${
                  d.is_today
                    ? 'bg-black hover:bg-black/80'
                    : 'bg-black/15 hover:bg-black/25'
                }`}
                style={{
                  height: `${heightPct}%`,
                  minHeight: '8px',
                  animationDelay: `${400 + i * 80}ms`,
                }}
              >
                <div className="absolute -top-7 left-1/2 hidden -translate-x-1/2 items-center justify-center whitespace-nowrap rounded-md bg-black px-2 py-1 text-[11px] font-bold text-white shadow-lg group-hover/bar:flex">
                  {d.count} tasks
                  <div className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 rounded-[1px] bg-black" />
                </div>
              </div>
            </div>
            <span
              className={`text-[13px] font-bold ${
                d.is_today ? 'text-black' : 'text-black/40'
              }`}
            >
              {d.day.charAt(0)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const OnYourPlateCard = ({ metrics }: { metrics: TaskMetrics }) => {
  const total = metrics.active || 1;
  const onTrackCount = Math.max(0, total - metrics.overdue - metrics.blocked);
  const onTrackPct = Math.round((onTrackCount / total) * 100);
  const overduePct = Math.round((metrics.overdue / total) * 100);
  const blockedPct = Math.round((metrics.blocked / total) * 100);

  const stats = [
    { label: 'On Track', pct: onTrackPct, count: onTrackCount, color: 'bg-emerald-500' },
    { label: 'Due Today', pct: Math.round((metrics.due_today / total) * 100), count: metrics.due_today, color: 'bg-blue-500' },
    { label: 'Overdue', pct: overduePct, count: metrics.overdue, color: 'bg-red-500' },
    { label: 'Blocked', pct: blockedPct, count: metrics.blocked, color: 'bg-amber-400' },
  ];

  return (
    <div className="flex h-full flex-col rounded-2xl bg-[#F7F7F8]">
      <div className="flex items-center gap-3 px-7 pb-4 pt-7">
        <ClipboardDocumentListIcon className="h-5 w-5 text-black" />
        <p className="text-xl font-bold tracking-tight text-black">
          On your plate
        </p>
      </div>
      <div className="flex flex-1 flex-col px-7 pb-6">
        {/* Stat chips with staggered scale-in animation */}
        <div className="flex flex-wrap gap-4 pb-4">
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="brief-stat flex flex-1 flex-col gap-1.5"
              style={{ animationDelay: `${200 + i * 100}ms` }}
            >
              <div className="flex items-center gap-1">
                <div className={`h-3 w-[3px] rounded-full ${s.color}`} />
                <p className="text-[11px] font-semibold text-black/50">{s.label}</p>
              </div>
              <p className="text-[28px] font-bold leading-none tracking-tight tabular-nums">
                {s.pct}%
              </p>
              <p className="text-[13px] font-semibold text-black/40 tabular-nums">
                {s.count}
              </p>
            </div>
          ))}
        </div>

        {/* Stacked bar */}
        <div className="flex h-[6px] w-full gap-0.5 overflow-hidden rounded-full">
          <div className="brief-bar-h h-full rounded-full bg-emerald-500" style={{ width: `${onTrackPct}%`, animationDelay: '500ms' }} />
          <div className="brief-bar-h h-full rounded-full bg-red-500" style={{ width: `${overduePct}%`, animationDelay: '560ms' }} />
          <div className="brief-bar-h h-full rounded-full bg-amber-400" style={{ width: `${blockedPct}%`, animationDelay: '620ms' }} />
        </div>

        {/* Weekly workload */}
        <div className="mt-5 flex-1 border-t border-black/[0.06] pt-5">
          <p className="mb-3 text-[13px] font-semibold text-black/50">Weekly Workload</p>
          {metrics.weekly_due && metrics.weekly_due.some((d) => d.count > 0) ? (
            <WeeklyBarChart data={metrics.weekly_due} />
          ) : (
            <div className="flex h-[120px] flex-col items-center justify-center gap-2">
              <p className="text-[32px] font-bold text-emerald-500 tabular-nums">0</p>
              <p className="text-[13px] font-medium text-black/40">Nothing due this week</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overdue Breakdown — tiered red bars with tooltips
// ---------------------------------------------------------------------------

const TieredBar = ({
  item,
  maxCount,
  index = 0,
}: {
  item: OverdueBreakdownItem;
  maxCount: number;
  index?: number;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const totalPct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
  const recentPct = item.count > 0 ? (item.overdue_recent / item.count) * 100 : 100;
  const severePct = item.count > 0 ? (item.overdue_30_plus / item.count) * 100 : 0;

  const tooltipPos =
    showTooltip && rowRef.current
      ? rowRef.current.getBoundingClientRect()
      : null;

  return (
    <div
      ref={rowRef}
      className="relative flex cursor-default items-center gap-1.5 py-[6px]"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {tooltipPos && createPortal(
        <div
          className="pointer-events-none fixed z-[9999]"
          style={{
            left: tooltipPos.left + 70,
            top: tooltipPos.top - 8,
            transform: 'translateY(-100%)',
          }}
        >
          <div className="min-w-[180px] rounded-xl bg-black px-4 py-3 text-white shadow-xl">
            <p className="mb-2 text-[14px] font-bold">{item.name}</p>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-[3px] rounded-full bg-[#D92D20]" />
                  <span className="text-[13px] font-medium text-white/80">0-30 days</span>
                </div>
                <span className="text-[13px] font-bold tabular-nums">{item.overdue_recent}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="h-3 w-[3px] rounded-full bg-[#7A271A]" />
                  <span className="text-[13px] font-medium text-white/80">30+ days</span>
                </div>
                <span className="text-[13px] font-bold tabular-nums">{item.overdue_30_plus}</span>
              </div>
            </div>
          </div>
          <div className="-mt-1.5 ml-6 h-3 w-3 rotate-45 rounded-[2px] bg-black" />
        </div>,
        document.body,
      )}
      <div className="w-[70px] min-w-[70px] overflow-hidden border-r border-black/[0.06] pr-1.5">
        <p className={`truncate text-[14px] font-semibold leading-tight transition-colors ${showTooltip ? 'text-black' : 'text-black/80'}`}>
          {item.name}
        </p>
      </div>
      <div className="flex flex-1 items-center justify-between gap-3 min-w-0">
        <div className="relative flex h-4 flex-1 min-w-0">
          <div className="absolute right-0 top-1/2 flex h-[0.5px] w-full items-center justify-end bg-black/[0.06]">
            <div className="h-[3px] w-[3px] rounded-full bg-black/[0.06]" />
          </div>
          <div
            className="brief-bar-h relative z-[1] flex h-4 overflow-hidden rounded-md origin-left"
            style={{ width: `${Math.max(totalPct, 6)}%`, animationDelay: `${300 + index * 60}ms` }}
          >
            <div className="h-full bg-[#D92D20]" style={{ width: `${recentPct}%` }} />
            {item.overdue_30_plus > 0 && (
              <div
                className="relative h-full overflow-hidden bg-[#7A271A]"
                style={{
                  width: `${severePct}%`,
                  backgroundImage:
                    'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(0,0,0,0.1) 4px, rgba(0,0,0,0.1) 6px)',
                }}
              />
            )}
          </div>
        </div>
        <p className={`w-6 flex-shrink-0 text-right text-[13px] font-semibold tabular-nums transition-colors ${showTooltip ? 'text-black/80' : 'text-black/50'}`}>
          {item.count}
        </p>
      </div>
    </div>
  );
};

const OverdueBreakdownCard = ({ metrics }: { metrics: TaskMetrics }) => {
  const matterMax = metrics.overdue_by_matter[0]?.count || 1;
  const typeMax = metrics.overdue_by_type[0]?.count || 1;

  if (metrics.overdue_by_matter.length === 0 && metrics.overdue_by_type.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-2xl bg-[#F7F7F8]">
        <p className="text-[15px] font-medium text-black/50">No overdue tasks</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col rounded-2xl bg-[#F7F7F8]">
      <div className="flex items-center justify-between px-7 pb-4 pt-7">
        <div className="flex items-center gap-3">
          <ChartBarIcon className="h-5 w-5 text-black" />
          <p className="text-xl font-bold tracking-tight text-black">Overdue Breakdown</p>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-semibold text-black/40">
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-sm bg-[#D92D20]" />
            <span>0-30d</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-sm bg-[#7A271A]" />
            <span>30d+</span>
          </div>
        </div>
      </div>
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-7 pb-6">
        <div className="flex gap-10">
          {metrics.overdue_by_matter.length > 0 && (
            <div className="min-w-0 flex-1">
              <p className="mb-2 text-[13px] font-bold text-black/60">By Matter</p>
              {metrics.overdue_by_matter.map((item, i) => (
                <TieredBar key={i} item={item} maxCount={matterMax} index={i} />
              ))}
            </div>
          )}
          {metrics.overdue_by_type.length > 0 && (
            <div className="min-w-0 flex-1">
              <p className="mb-2 text-[13px] font-bold text-black/60">By Task Type</p>
              {metrics.overdue_by_type.map((item, i) => (
                <TieredBar key={i} item={item} maxCount={typeMax} index={i} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

const EmptyState = () => (
  <div className="flex flex-1 flex-col items-center justify-center text-center">
    <div className="mb-8 flex h-20 w-20 items-center justify-center rounded-3xl bg-black/[0.03]">
      <CalendarDaysIcon className="h-9 w-9 text-black/40" />
    </div>
    <h2 className="text-2xl font-bold tracking-tight text-black">No brief for today</h2>
    <p className="mt-3 max-w-sm text-[15px] font-medium leading-relaxed text-black/50">
      Your brief is generated each morning at 8 AM EST.<br />Check back tomorrow.
    </p>
  </div>
);

const LoadingState = () => (
  <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
    <Loader2 className="h-8 w-8 animate-spin text-black/40" strokeWidth={2} />
    <p className="text-[15px] font-semibold text-black/60">Generating brief...</p>
    <p className="text-[13px] font-medium text-black/50">This may take 10-15 seconds</p>
  </div>
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatSourceName = (raw: string): string =>
  raw
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const PresidentialBriefPage = () => {
  useInjectStyles();
  const user = useSelector(selectUser);
  const now = useLiveClock();
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const generateParam = searchParams.get('generate');

  const queryString = [
    emailParam ? `email=${encodeURIComponent(emailParam)}` : '',
    generateParam === 'true' ? 'generate=true' : '',
  ]
    .filter(Boolean)
    .join('&');

  const {
    data: brief,
    isLoading,
    isFetching,
    isError,
  } = useGetTodaysBriefQuery(queryString || undefined);
  const [submitThumbsDown] = useSubmitThumbsDownMutation();
  const [thumbsDownSubmitted, setThumbsDownSubmitted] = useState(false);

  const isGenerating = generateParam === 'true';
  const showLoading = isGenerating ? (isLoading || isFetching) : isLoading && !brief;

  let displayName = '';
  if (emailParam) {
    displayName = emailParam
      .split('@')[0]
      .replace(/\./g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  } else if (user) {
    displayName = `${user.first_name} ${user.last_name}`.trim() || user.email;
  }

  const handleThumbsDown = async () => {
    if (thumbsDownSubmitted) return;
    await submitThumbsDown({});
    setThumbsDownSubmitted(true);
  };

  const metrics = brief?.task_metrics;

  return (
    <div className="mx-auto flex h-screen w-full max-w-[96rem] flex-col overflow-hidden bg-white">
      <header className="flex flex-shrink-0 items-center justify-between border-b border-black/[0.06] px-10 py-5">
        <div className="flex items-center gap-5">
          <h1 className="text-[32px] font-bold tracking-tight text-black">Brief</h1>
          <div className="h-10 w-px bg-black/10" />
          <div>
            <p className="text-[15px] font-semibold capitalize leading-tight text-black">
              {displayName}
            </p>
            <div className="mt-0.5 flex items-center gap-2">
              <time className="text-[13px] font-medium uppercase tabular-nums text-black/50">
                {format(now, 'h:mm:ss a')} EST
              </time>
              <span className="text-[13px] font-medium text-black/50">
                {format(now, 'EEE, MMM d')}
              </span>
            </div>
          </div>
        </div>
        <a
          href="/tasks"
          className="flex h-[38px] items-center rounded-full bg-black px-5 text-[13px] font-bold text-white transition-all duration-150 hover:bg-black/80"
        >
          Go to Finch
        </a>
      </header>

      <main className="flex min-h-0 flex-1 flex-col overflow-hidden px-10 py-6">
        {showLoading && <LoadingState />}
        {!showLoading && (isError || !brief) && <EmptyState />}
        {!showLoading && brief && (
          <>
            <div className="grid min-h-0 flex-1 grid-cols-3 grid-rows-2 gap-6">
              <div className="brief-card brief-card-d1 min-h-0">
                <ForYouCard items={brief.narrative.for_you} />
              </div>
              <div className="brief-card brief-card-d2 min-h-0">
                <BlockedByYouCard items={metrics?.blocked_by_you || []} />
              </div>
              <div className="brief-card brief-card-d3 row-span-2 min-h-0">
                {metrics ? (
                  <OnYourPlateCard metrics={metrics} />
                ) : (
                  <div className="h-full rounded-2xl bg-[#F7F7F8]" />
                )}
              </div>
              <div className="brief-card brief-card-d4 col-span-2 min-h-0">
                {metrics ? (
                  <OverdueBreakdownCard metrics={metrics} />
                ) : (
                  <div className="h-full rounded-2xl bg-[#F7F7F8]" />
                )}
              </div>
            </div>

            <div className="brief-footer mt-4 flex flex-shrink-0 items-center justify-between border-t border-black/[0.06] pt-3">
              <div className="flex items-center gap-4">
                <span className="text-[11px] font-semibold text-black/40">
                  Brief generated for {format(parseISO(brief.brief_date), 'MMM d, yyyy')}
                </span>
                <div className="group/sources relative flex cursor-default items-center gap-1.5 text-[11px] font-semibold text-black/40">
                  <InformationCircleIcon className="h-3.5 w-3.5" />
                  <span>{brief.context_sources_used.length} sources</span>
                  <div className="absolute bottom-full left-0 z-[9999] mb-2 hidden group-hover/sources:block">
                    <div className="min-w-[220px] max-w-[320px] rounded-xl bg-black px-4 py-3 text-white shadow-xl">
                      <p className="mb-2 text-[12px] font-bold uppercase tracking-wider text-white/60">
                        Context Sources
                      </p>
                      <div className="flex flex-col gap-1.5">
                        {brief.context_sources_used.map((src) => (
                          <div key={src} className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                            <span className="text-[13px] font-medium text-white/90">
                              {formatSourceName(src)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="-mt-1.5 ml-4 h-3 w-3 rotate-45 rounded-[2px] bg-black" />
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleThumbsDown}
                disabled={thumbsDownSubmitted || brief.thumbs_down}
                className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all duration-150 ${
                  thumbsDownSubmitted || brief.thumbs_down
                    ? 'cursor-not-allowed text-black/40'
                    : 'cursor-pointer text-black/50 hover:bg-red-50 hover:text-red-500'
                }`}
              >
                <HandThumbDownIcon className="h-3.5 w-3.5" />
                {thumbsDownSubmitted || brief.thumbs_down ? 'Sent' : 'Not helpful'}
              </button>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default PresidentialBriefPage;
